import { UIDialogData } from 'app/ui/dialog/dialog-injector';
import { DocumentType } from './sample.model';
import {
  Component,
  ViewChild,
  ElementRef,
  HostListener,
  Inject
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { OnInit } from '@angular/core';

import { samples as allSamples } from './samples';
import { Sample } from 'app/orders/wizard/use-examples/sample.model';
import { UIDialogService } from 'app/ui/dialog/dialog.service';
import { UserSettingsService } from 'app/user/user-settings.service';
import { WizardService } from 'app/orders/wizard/wizard.service';
import { OrderSettings } from 'app/orders/settings/settings.model';
import { FileService } from 'app/files/file.service';
import { DownloadService, isIE, isEdge } from 'app//files/download.service';
import { IInputFile } from 'app/files/file.model';
import { camelCase } from 'app/core/case-transform';

@Component({
  selector: 'wizard-use-examples',
  templateUrl: 'use-examples.component.html',
  styleUrls: ['use-examples.component.scss']
})
export class WizardUseExamplesComponent implements OnInit {
  error: string;
  errorDescription: string;

  private _processing: boolean;

  orderSettings: OrderSettings;

  private sampleWidth = 120;

  @ViewChild('samplesList')
  samplesList: ElementRef;

  samples: Sample[];

  get processing(): boolean {
    return this._processing;
  }

  get visibleRegion(): boolean {
    return this._userSettingsService.serviceType === 'ic';
  }

  private get samplesElement(): HTMLElement {
    return <HTMLElement>this.samplesList.nativeElement;
  }

  documentsTypes = [
    {
      key: DocumentType.Invoices,
      value: 'Invoices'
    }
  ];

  // Выбранный тип документа
  private documentType: string = this.documentsTypes[0].key;

  constructor(
    @Inject(UIDialogData)
    public sample: Sample,
    private _dialogService: UIDialogService,
    private _userSettingsService: UserSettingsService,
    private _fileService: FileService,
    private _downloadService: DownloadService,
    private _wizardService: WizardService
  ) { }

  ngOnInit() {
    this.orderSettings = Object.assign(
      {},
      this._userSettingsService.orderSettings
    );
    if (this.orderSettings.verificationType === 'Troubleshooting') {
      this.orderSettings.verificationType = 'UserVerification';
    }

    if (this._userSettingsService.serviceType === 'dc') {
      this.documentsTypes.push(
        {
          key: DocumentType.ApplicationForms,
          value: 'Application Forms'
        },
        {
          key: DocumentType.Contracts,
          value: 'Contracts'
        },
        {
          key: DocumentType.Letters,
          value: 'Letters'
        }
      );
      this.documentType = this.documentsTypes[1].key;
    }
  }

  async onSubmit(form: FormControl) {
    this.onHideError();

    if (!form.valid) {
      return;
    }

    try {
      this._processing = true;

      const fileBlob = await this._downloadService.download(
        this.sample.fileUrl, undefined
      );
      const fileSource =
        isIE() || isEdge()
          ? fileBlob
          : new File([fileBlob], this.sample.fileName);
      const inputFile = <IInputFile>{
        name: this.sample.fileName,
        size: this.sample.size,
        extension: this.sample.extension,
        source: fileSource
      };
      const file = await this._fileService.upload(inputFile).toPromise();
      inputFile.id = file.id;
      inputFile.token = file.token;
      this._userSettingsService.orderSettings = Object.assign(
        {},
        this.orderSettings
      );
      this._wizardService.files = [
        Object.assign({}, inputFile, { progress: 100 })
      ];
      this._wizardService
        .create(
          {
            export_format: this.orderSettings.exportFormat,
            region: this.sample.region || this.orderSettings.region,
            verification: this.orderSettings.verificationType,
            document_type: this._userSettingsService.documentType,
          },
          this._userSettingsService.serviceType
        )
        .subscribe(
          () => {
            this._wizardService.reset();
            this._dialogService.close();
          },
          error => this._onError(error)
        );
    } catch (error) {
      this._onError(error);
    }
  }

  isSelectedType(type: string): boolean {
    return type === this.documentType;
  }

  isSelected(sample: Sample): boolean {
    return sample === this.sample;
  }

  canMoveNext() {
    return (
      this.samplesElement.scrollLeft <
      this.samplesElement.scrollWidth - this.samplesElement.clientWidth
    );
  }

  canMovePrev() {
    return this.samplesElement.scrollLeft > 0;
  }

  nextSample(): void {
    this.addScroll(this.samplesList, this.sampleWidth);
  }

  prevSample(): void {
    this.addScroll(this.samplesList, -this.sampleWidth);
  }

  onMouseWheel(delta: number) {
    this.addScroll(this.samplesList, -delta * 50);
  }

  private addScroll(elementRef: ElementRef, scroll: number) {
    const element = <HTMLElement>elementRef.nativeElement;
    element.scrollLeft += scroll;
  }

  @HostListener('document:keydown.escape')
  private close() {
    this._dialogService.close();
  }

  regionName(region: string): string {
    switch (region) {
      case 'AU':
        return 'Australia';
      case 'CA':
        return 'Canada';
      case 'EU':
        return 'European Union';
      case 'US':
        return 'USA';
      default:
        return 'Unknown';
    }
  }

  onHideError(): void {
    this.error = null;
    this.errorDescription = null;
  }

  private _onError(response): void {
    if (response.status === 400) {
      const responseJson = response.json();
      this.error = camelCase(responseJson.error);
      this.errorDescription = this._getErrorDescription(responseJson);
    }
    this._processing = false;
  }

  private _getErrorDescription(responseJson): string {
    const modelState = responseJson.modelState;
    if (!modelState) {
      return responseJson.errorDescription;
    }
    return Object.keys(modelState)
      .map(fieldName => modelState[fieldName].join('\n'))
      .join('\n');
  }
}
